import { z } from 'zod/mini';
import { CartAndLineItemGroups, LineItem } from '@/types/commercetools';
import { getLineItemTotalPrice } from '@/utility/lineItems/lineItemPrices';

// Request and response of pricing-service `POST /api/v1/pricing/validation`.
// Business unit the coupons of this storefront are validated for.
export const BUSINESS_UNIT = 'CYO';

export type ValidationItem = {
  ref: string;
  minorUnitPrice: number;
  quantity: number;
  productTypeId: number;
  productTypeCategoryIds: number[];
  volumeDiscount: boolean;
  businessUnit: typeof BUSINESS_UNIT;
};

export type CouponValidationRequest = {
  baskets: {
    ref: string;
    currency: string;
    timezone: string;
    coupons: { code: string }[];
    items: ValidationItem[];
  }[];
};

const CouponValidationResponseSchema = z.object({
  baskets: z.array(z.object({ couponsApplied: z.array(z.object({ code: z.string() })) })),
});

// product type id → ids of all categories containing it
export type CategoryIdsByProductType = Map<string, number[]>;

export const getCategoryIdsByProductType = (
  departments: ProductTypeDepartment[]
): CategoryIdsByProductType => {
  const categoryIds: CategoryIdsByProductType = new Map();

  departments
    .flatMap((department) => department.categories)
    .forEach((category) =>
      category.productTypes.forEach(({ id }) =>
        categoryIds.set(id, [...(categoryIds.get(id) ?? []), Number(category.id)])
      )
    );

  return categoryIds;
};

const toValidationItem = (
  lineItem: LineItem,
  cart: CartAndLineItemGroups['cart'],
  productTypeId: number,
  categoryIds: CategoryIdsByProductType | undefined
): ValidationItem => {
  const { totalPrice, totalVolumeDiscount } = getLineItemTotalPrice(lineItem, cart);

  return {
    ref: lineItem.id,
    // `totalPrice` is undiscounted; take off the volume discount, but no coupon discounts.
    minorUnitPrice: Math.round((totalPrice - totalVolumeDiscount) / lineItem.quantity),
    quantity: lineItem.quantity,
    productTypeId,
    productTypeCategoryIds: categoryIds?.get(String(productTypeId)) ?? [],
    volumeDiscount: totalVolumeDiscount > 0,
    businessUnit: BUSINESS_UNIT,
  };
};

// One validation item per cart line item. `categoryIdsByShop` is keyed by shop id.
export const getCartValidationItems = (
  { cart, lineItemGroups }: CartAndLineItemGroups,
  categoryIdsByShop: Record<string, CategoryIdsByProductType>
): ValidationItem[] =>
  lineItemGroups
    .flatMap((group) =>
      group.lineItems.map((lineItem) =>
        toValidationItem(
          lineItem,
          cart,
          group.productTypeId,
          categoryIdsByShop[String(group.shopId)]
        )
      )
    )
    // the service rejects the whole request for a non-positive price
    .filter((item) => item.minorUnitPrice > 0);

export const buildValidationRequest = ({
  cartAndLineItemGroups,
  coupons,
  categoryIdsByShop,
  timezone,
}: {
  cartAndLineItemGroups: CartAndLineItemGroups;
  coupons: string[];
  // keyed by shop id
  categoryIdsByShop: Record<string, CategoryIdsByProductType>;
  timezone: string;
}): CouponValidationRequest => ({
  baskets: [
    {
      ref: cartAndLineItemGroups.cart.id,
      currency: cartAndLineItemGroups.cart.totalPrice.currencyCode,
      timezone,
      coupons: coupons.map((code) => ({ code })),
      items: getCartValidationItems(cartAndLineItemGroups, categoryIdsByShop),
    },
  ],
});

// Returns the codes of the coupons the pricing service applied to the (only) basket.
export const parseValidationResponse = (response: unknown): string[] => {
  const [basket] = CouponValidationResponseSchema.parse(response).baskets;
  if (!basket) {
    throw new Error('Coupon validation response contains no basket');
  }
  return basket.couponsApplied.map((c) => c.code);
};
